import type { Ref } from 'vue';

/**
 * Shared Tab/Shift-Tab focus-trap logic for modal-like overlays (LgModal,
 * LgDrawer). Cycles focus among the focusable descendants of `container`;
 * when there are none, keeps focus pinned on the container itself.
 *
 * Escape handling and initial-focus timing are left to the caller since they
 * differ slightly between consumers (e.g. LgModal also closes on Escape from
 * this same keydown handler).
 */
const FOCUSABLE =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

export function useFocusTrap(container: Ref<HTMLElement | null>) {
  function focusables(): HTMLElement[] {
    if (!container.value) return [];
    // The `:not([disabled])` in the selector already drops disabled controls;
    // our overlays never hide focusables, so no visibility filter is needed
    // (and offsetParent-based checks are unreliable under jsdom/no-layout
    // anyway).
    return Array.from(container.value.querySelectorAll<HTMLElement>(FOCUSABLE));
  }

  /** Focus the first focusable descendant, falling back to the container. */
  function focusFirst() {
    const items = focusables();
    (items[0] ?? container.value)?.focus();
  }

  /** Call from a keydown handler; traps Tab/Shift-Tab within `container`. */
  function onKeydown(e: KeyboardEvent) {
    if (e.key !== 'Tab') return;
    const items = focusables();
    if (!items.length) {
      e.preventDefault();
      container.value?.focus();
      return;
    }
    const first = items[0];
    const last = items[items.length - 1];
    const active = document.activeElement as HTMLElement;
    if (e.shiftKey && (active === first || !container.value?.contains(active))) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && active === last) {
      e.preventDefault();
      first.focus();
    }
  }

  return { focusables, focusFirst, onKeydown };
}
